import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { MsalAzureLoginService } from '../../services/msal-azure-login.service';
import { CurrentUserService } from '../../services/current-user.service';
import { TenantBrandingService } from '../../ui/tenant-branding.service';
import { NotificationBellComponent } from '../notification-bell/notification-bell.component';
import { OnboardingStateService } from '../../../onboarding/services/onboarding-state.service';
import { OnboardingStep } from '../../../onboarding/models/onboarding.models';

type HeaderMode = 'signup' | 'portal' | 'plain';

interface NavItem {
  label: string;
  path: string;
  /** URL prefixes that count as this item being the current page. */
  matches: string[];
}

/**
 * The ONE header: the tenant's logo and name (the portal is white-labelled), then
 *  - sign-up (/register): "Client onboarding · powered by Bizcombo" and Sign in / Sign out;
 *  - portal (/auth/*): the main nav, notifications and the user menu.
 * It replaces the old dark "Your Company" bar and the wizard's own "Bizcombo" bar.
 */
@Component({
  selector: 'app-header',
  imports: [RouterLink, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, MatDivider, NotificationBellComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly msalAzureLoginService = inject(MsalAzureLoginService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly signupState = inject(OnboardingStateService);
  protected readonly tenant = inject(TenantBrandingService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  protected readonly mode = computed<HeaderMode>(() => {
    const url = this.url();
    // Waiting for access to an existing client: no portal nav yet, just the firm and Sign out.
    if (url.startsWith('/register') || url.startsWith('/auth/access-request')) return 'signup';
    if (url.startsWith('/auth/')) return 'portal';
    return 'plain';
  });

  protected readonly nav = computed<NavItem[]>(() => [
    {
      label: 'Overview',
      path: MsalAzureLoginService.dashboardPath(),
      matches: ['/auth/dashboard', '/auth/individual-dashboard'],
    },
    { label: 'Application', path: '/auth/form', matches: ['/auth/form'] },
  ]);

  protected isCurrent(item: NavItem): boolean {
    const url = this.url();
    return item.matches.some((m) => url.startsWith(m));
  }

  /** On the sign-up screens, before the account is finished: offer sign-in to people who have one. */
  protected readonly showSignIn = computed(
    () => !this.isSignedIn() && this.signupState.step() < OnboardingStep.Complete,
  );

  protected isSignedIn(): boolean {
    return !!this.msalAzureLoginService.activeAccount;
  }

  /** First name: the saved profile first (B2C local-account tokens rarely carry given_name). */
  private get firstName(): string {
    return this.currentUser.firstName()
      || (this.msalAzureLoginService.activeAccount?.idTokenClaims?.['given_name'] as string) || '';
  }

  private get lastName(): string {
    return this.currentUser.lastName()
      || (this.msalAzureLoginService.activeAccount?.idTokenClaims?.['family_name'] as string) || '';
  }

  /** The person signed in. The company goes on the second line, not in place of their name. */
  get userName(): string {
    const full = `${this.firstName} ${this.lastName}`.trim();
    if (full) return full;
    const account = this.msalAzureLoginService.activeAccount;
    return account?.name || account?.username?.split('@')[0] || this.userEmail;
  }

  get companyName(): string {
    return this.currentUser.companyName();
  }

  get userInitials(): string {
    if (this.firstName && this.lastName) return (this.firstName[0] + this.lastName[0]).toUpperCase();
    const parts = this.userName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return this.userName.substring(0, 2).toUpperCase();
  }

  /** B2C stores emails as an array claim. */
  get userEmail(): string {
    const account = this.msalAzureLoginService.activeAccount;
    const emails = account?.idTokenClaims?.['emails'] as string[] | undefined;
    return emails?.[0] || account?.username || '';
  }

  ngOnInit(): void {
    // Load the saved profile once so the header shows the real name and company, but only once
    // someone is signed in: the call needs a token, and on the public landing page the token
    // interceptor would otherwise send a visitor straight to the B2C sign-in page. Checked again on
    // each navigation, since the account is set when the sign-in redirect is handled.
    const loadIfSignedIn = () => {
      if (this.msalAzureLoginService.activeAccount) this.currentUser.load();
    };
    loadIfSignedIn();
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(loadIfSignedIn);
  }

  protected homePath(): string {
    return this.mode() === 'portal' ? MsalAzureLoginService.dashboardPath() : '/';
  }

  protected goToSettings(): void {
    this.router.navigate(['/auth/account']);
  }

  /** The platform signs in through Azure AD B2C from the landing page. */
  protected signIn(): void {
    window.location.assign('/');
  }

  protected logout(): void {
    // Mid-sign-up, also drop the wizard's saved progress so the next person starts clean.
    if (this.mode() === 'signup') this.signupState.reset();
    this.msalAzureLoginService.logout();
  }
}
